// src/components/Notification/index.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { XMarkIcon, CheckCircleIcon, AlertTriangleIcon, InfoCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// 定义通知类型
export type NotificationType = 'success' | 'warning' | 'error' | 'info';

// 定义单个通知项的属性接口
export interface NotificationProps {
    id: string | number; // ID 是必填项，由管理器保证
    type: NotificationType;
    message: string;
    title?: string;
    duration?: number; // 自动关闭时间(ms)，0 表示不自动关闭
    onClose: (id: string | number) => void; // 关闭通知的回调函数
    onClick?: () => void; // 点击通知的回调函数
    isClosable?: boolean; // 是否显示关闭按钮

    onImmediateHandle?: () => void; // 立即处理回调

    onLaterHandle?: () => void;    // 稍后处理回调
}

const Notification: React.FC<NotificationProps> = ({
                                                       id,
                                                       type,
                                                       message,
                                                       title,
                                                       duration = 3000,
                                                       onClose,
                                                       onClick,
                                                       isClosable = true,
                                                       onImmediateHandle,
                                                       onLaterHandle
                                                   }) => {
    const [isVisible, setIsVisible] = useState(true);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // 关闭通知
    const closeNotification = useCallback(() => {
        setIsVisible(false);
        // 延迟执行 onClose，等待消失动画完成
        setTimeout(() => {
            onClose(id);
        }, 300); // 与 CSS transition 时间保持一致
    }, [id, onClose]);

    // 自动关闭逻辑
    useEffect(() => {
        if (duration > 0) {
            timerRef.current = setTimeout(closeNotification, duration);
        }

        // 组件卸载时清除定时器
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [duration, closeNotification]);

    // 鼠标悬停时暂停自动关闭，离开时恢复
    const handleMouseEnter = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (duration > 0) {
            timerRef.current = setTimeout(closeNotification, duration);
        }
    }, [duration, closeNotification]);

    // 根据类型获取图标和样式
    const getIconAndStyles = useCallback(() => {
        switch (type) {
            case 'success':
                return {
                    // icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
                    bgColor: 'bg-green-50 border border-green-200',
                    textColor: 'text-green-800',
                    progressColor: 'bg-green-500',
                };
            case 'warning':
                return {
                    // icon: <AlertTriangleIcon className="h-5 w-5 text-amber-500" />,
                    bgColor: 'bg-amber-50 border border-amber-200',
                    textColor: 'text-amber-800',
                    progressColor: 'bg-amber-500',
                };
            case 'error':
                return {
                    // icon: <ExclamationCircleIcon className="h-5 w-5 text-red-500" />,
                    bgColor: 'bg-red-50 border border-red-200',
                    textColor: 'text-red-800',
                    progressColor: 'bg-red-500',
                };
            case 'info':
            default:
                return {
                    // icon: <InfoCircleIcon className="h-5 w-5 text-blue-500" />,
                    bgColor: 'bg-blue-50 border border-blue-200',
                    textColor: 'text-blue-800',
                    progressColor: 'bg-blue-500',
                };
        }
    }, [type]);

    // const { icon, bgColor, textColor, progressColor } = getIconAndStyles();

    const {bgColor, textColor, progressColor } = getIconAndStyles();

    // 如果不可见，则不渲染
    if (!isVisible) return null;

    return (
        <div
            className={`
        w-80 rounded-md ${bgColor} ${textColor} p-4 shadow-lg transform transition-all duration-300 ease-in-out
        hover:shadow-xl
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
            style={{
                animation: 'slideIn 0.3s forwards',
            }}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="alert"
        >

            <div className="flex items-start justify-between">

                {/*<div className="flex items-start gap-3 flex-1 min-w-0">*/}
                {/*    /!*{icon}*!/*/}
                {/*    <div>*/}
                {/*        {title && <h4 className="font-semibold text-sm mb-1 truncate">{title}</h4>}*/}
                {/*        <p className="text-sm leading-relaxed truncate">{message}</p>*/}
                {/*    </div>*/}
                {/*</div>*/}

                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div>
                        {title && <h4 className="font-semibold text-sm mb-1 truncate">{title}</h4>}
                        <p className="text-sm leading-relaxed truncate">{message}</p>

                        {/* 按钮区域 - 只有当提供了至少一个回调时才显示 */}
                        {(onImmediateHandle || onLaterHandle) && (
                            <div className="flex gap-2 mt-3">
                                {onImmediateHandle && (
                                    <button
                                        className="px-3 py-1 text-xs bg-white rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onImmediateHandle!();
                                            closeNotification(); // 处理后关闭通知
                                        }}
                                    >
                                        立即处理
                                    </button>
                                )}
                                {onLaterHandle && (
                                    <button
                                        className="px-3 py-1 text-xs bg-gray-100 rounded border border-gray-300 hover:bg-gray-200 transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onLaterHandle!();
                                            closeNotification(); // 处理后关闭通知
                                        }}
                                    >
                                        稍后处理
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>


                {isClosable && (
                    <button
                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                        onClick={(e) => {
                            e.stopPropagation(); // 阻止事件冒泡到通知本身
                            closeNotification();
                        }}
                        aria-label="Close notification"
                    >
                        {/*<XMarkIcon className="h-4 w-4" />*/}
                    </button>
                )}
            </div>
            {duration > 0 && (
                <div className="mt-3 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className={`h-full ${progressColor} transition-all linear`}
                        style={{
                            width: '100%',
                            animationDuration: `${duration}ms`,
                        }}
                    />
                </div>
            )}
            <style>
{`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          .transition-all.linear {
            animation: progressBar ${duration}ms linear forwards;
          }
          @keyframes progressBar {
            from { width: 100%; }
            to { width: 0; }
          }
        
`}</style>
        </div>
    );
};

// 使用 React.memo 优化性能，避免不必要的重渲染
export default React.memo(Notification);
